import React from 'react'
import { bool, func, node, oneOf, object, string } from 'prop-types'
import Link from 'next/link'

/**
 * SmartLink can be used in place of any clickable element and will output
 * the correct HTML element for the props provided to it.
 *
 * At a basic level, it will output:
 *
 * - `<a />` if passed an `href`
 * - `<Link />` if passed a `to` (relies on third-party component)
 * - `<button />` if passed a `type` or `onClick`.
 *
 * It will also embellish output as required, for example automatically
 * adding `rel="noopener"` to any link with `target="_blank"`.
 */

const SmartLink = ({
  children,
  className,
  disabled,
  href,
  setRef,
  to,
  target,
  type,
  style,
  ...other
}) => {
  // Standard link, use an `anchor` element
  if (href) {
    return (
      <a
        ref={setRef}
        className={className}
        {...(!disabled && { href })}
        {...(disabled && { 'aria-disabled': 'true' })}
        {...(target && { target })}
        {...(target === '_blank' && { rel: 'noopener' })}
        {...other}
        style={style}
      >
        {children}
      </a>
    )
  }

  // Internal link, use third-party `Link` component from router module
  if (to) {
    return (
      <Link href={to} ref={setRef} {...other}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a className={className} style={style}>
          {children}
        </a>
      </Link>
    )
  }

  // No `href` or `to` means we need a `button` element
  return (
    <button
      type={type}
      ref={setRef}
      className={className}
      {...(disabled && { disabled })}
      style={style}
      {...other}
    >
      {children}
    </button>
  )
}

SmartLink.defaultProps = {
  type: 'button'
}

SmartLink.propTypes = {
  children: node.isRequired,
  className: string,
  disabled: bool,
  href: string,
  setRef: func,
  to: string,
  target: string,
  style: object,
  type: oneOf(['button', 'reset', 'submit'])
}

export default SmartLink
